import React, { FC, useCallback } from 'react';
import _ from 'lodash';
import querystring from 'query-string';
import { connect } from 'react-redux';
import { Col, Form, Row } from 'antd';
import { BaseStore, ActionCreatorType } from 'src/store';
import {
  searchProducts as _searchProducts,
  searchSuggestions as _searchSuggestions,
} from '../../../store/actions';
import { Locale, useLocale } from 'src/modules/locale';
import { ProductLocalizedEntity } from 'src/modules/products';
import { rowDefaultProps } from 'src/utils/grid';
import { getCurrentUrlParams } from 'src/utils/url';
import { DropdownSuggestions } from './suggestions/DropdownSuggestions';
import styles from './ProfessionalsDownloadsProductsSearchIndex.module.scss';
import { Filters } from 'src/modules/form/field/filters/Filters/Filters';
import { SearchInput } from 'src/modules/form/field/SearchInput/SearchInput';
import { useRouter } from 'next/router';
import { getQueryFromFilters, getFiltersFromQuery } from 'src/modules/form/field/filters/utils';
import { FilterMenuItem } from 'src/modules/form/field/filters/types';
import { ProductEntity, ProductCategoryEntity } from 'src/modules/products';
import { LocationTypeEntity } from 'src/modules/locationTypes';
import { useChannelFilteredData } from 'src/utils/cleanupProducts';

interface ProfessionalsDownloadsProductsSearchIndexProps {
  useFilters?: boolean;
  redirect?: boolean;
  searchSuggestions: ActionCreatorType;
  searchProducts: ActionCreatorType;
  results: ProductEntity[];
  productCategories?: ProductCategoryEntity[];
  locationTypes?: LocationTypeEntity[];
  searchSuggestionsResults: ProductEntity[];
}

const ProfessionalsDownloadsProductsSearchIndex: FC<ProfessionalsDownloadsProductsSearchIndexProps> = (
  props
) => {
  const {
    results: rawResults,
    searchProducts,
    searchSuggestions,
    productCategories: rawProductCategories,
    locationTypes: rawLocationTypes,
    searchSuggestionsResults: rawSearchSuggestionsResults = [],
    useFilters = true,
    redirect = false,
  } = props;

  const router = useRouter();

  const { getLocalizedObjects } = useLocale();

  const [form] = Form.useForm();
  const results = useChannelFilteredData(rawResults);
  const searchSuggestionsResults = useChannelFilteredData(rawSearchSuggestionsResults);
  const searchResultsCount = results.length;

  const productCategories = getLocalizedObjects(rawProductCategories || []).sort((a, b) =>
    a.name.localeCompare(b.name)
  ) as FilterMenuItem[];

  const locationTypes = (getLocalizedObjects(rawLocationTypes) || []).sort((a, b) =>
    a.name.localeCompare(b.name)
  ) as FilterMenuItem[];

  const localizedSearchSuggestionResults = getLocalizedObjects<ProductLocalizedEntity>(
    searchSuggestionsResults
  );
  const { category, room } = querystring.parse(getCurrentUrlParams());
  const queryFilters = getFiltersFromQuery(
    { productCategories: rawProductCategories as any, locationTypes: rawLocationTypes as any },
    { productCategories: category, locationTypes: room }
  );

  const handleFormSubmit = async ({ term, categories, locationTypes }) => {
    if (useFilters) {
      router.push(
        {
          query: getQueryFromFilters(
            { ...router.query, search: term },
            { category: categories, room: locationTypes }
          ),
        },
        undefined,
        { shallow: true }
      );

      searchProducts({
        filters: {
          term,
          locale: Locale.EN,
          productCategories: categories,
          locationTypes,
        },
      });
    } else {
      router.push({
        pathname: '/professionals/downloads/products',
        query: { search: term },
      });
    }
  };

  const updateSearchSuggestions = useCallback(
    (term) => {
      searchSuggestions({
        term,
        categories: form.getFieldValue('categories'),
        locationTypes: form.getFieldValue('locationTypes'),
      });
    },
    [form, searchSuggestions]
  );

  //Save the location types which have products to display them in the filters
  const availableLocationTypes = results.length
    ? [
        ...new Set(
          results.reduce(
            (acc, product) => acc.concat(product.location_types.map((locType) => locType.id)),
            []
          )
        ),
      ]
    : locationTypes;

  //Save the product subcategories which have products to display them in the filters
  const availableProductCategories = results.length
    ? [
        ...new Set(
          results.reduce(
            (acc, product) =>
              acc.concat(
                // There is a bug with getLocalizedObject, which converts product subcategory IDs into arrays. Object.values(subcat).join converts it back to a string
                product.product_categories?.map((cat) => cat.id)
              ),
            []
          )
        ),
      ]
    : productCategories;

  const categoriesFilters = queryFilters.productCategories.length
    ? productCategories.filter((subcat) => availableProductCategories.includes(subcat.id))
    : productCategories;

  const locationTypeFilters = queryFilters.locationTypes.length
    ? locationTypes.filter((locationType) => availableLocationTypes.includes(locationType.id))
    : locationTypes;
  return (
    <div>
      <Form
        form={form}
        onFinish={_.debounce<any>(handleFormSubmit, 500)}
        initialValues={{
          term: router.query?.search || '',
          // ...queryFilters,
          categories: queryFilters.productCategories,
          locationTypes: queryFilters.locationTypes,
          // locationTypes: [],
        }}>
        <Row {...rowDefaultProps}>
          <Col lg={8} md={8} sm={24} xs={24}>
            <SearchInput
              name="term"
              form={form}
              suggestions={localizedSearchSuggestionResults}
              updateSuggestions={updateSearchSuggestions}
              searchOnDelete={!redirect}
              placeholderKey="page/professionals/downloads/products/search/search_placeholder"
              getValueFromSuggestion={(product) => `${product.name}`}
              enforceSuggestionValue={false}
              onSuggestionSelectedByEnterPress={(product) => {
                router.push(`/professionals/downloads/products/${product.slug}`);
              }}>
              {({ selectedSuggestionIndex }) => (
                <DropdownSuggestions
                  selectedIndex={selectedSuggestionIndex}
                  products={localizedSearchSuggestionResults}
                />
              )}
            </SearchInput>
          </Col>
          {useFilters && (
            <Col lg={16} md={16} sm={24} xs={24}>
              <Filters
                form={form}
                filters={{
                  categories: {
                    labelKey: 'filters/filter_labels/categories',
                    items: categoriesFilters,
                  },
                  locationTypes: {
                    labelKey: 'filters/filter_labels/rooms',
                    items: locationTypeFilters,
                  },
                }}
                resultsCountKey="page/professionals/downloads/products/search/search_results_count"
                resultsCount={searchResultsCount}
                largeScreenFiltersClassName={styles.largeScreenFilters}
                searchCountClassName={styles.searchCountClassName}
              />
            </Col>
          )}
        </Row>
      </Form>
    </div>
  );
};

const mapDispatchToProps = {
  searchProducts: _searchProducts,
  searchSuggestions: _searchSuggestions,
};

const mapStateToProps = (state: BaseStore) => {
  const products = state.professionals.downloads.products;
  return {
    results: products.results,
    searchSuggestionsResults: products.searchSuggestionsResults,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProfessionalsDownloadsProductsSearchIndex);
